/**
 * Lightweight Custom Carousel
 * A minimal carousel solution for homepage performance
 */

class LightweightCarousel {
  constructor(container, options = {}) {
    this.container = container;
    this.wrapper = container.querySelector('.swiper-wrapper') || container.querySelector('[data-carousel-wrapper]');
    // Only count direct children that have the swiper-slide class
    const allChildren = Array.from(this.wrapper?.children || []);
    this.slides = allChildren.filter(child => child.classList.contains('swiper-slide'));

    if (!this.wrapper || this.slides.length === 0) return;

    // Default options
    this.options = {
      slidesPerView: options.slidesPerView || 1,
      spaceBetween: options.spaceBetween || 0,
      loop: options.loop || false,
      autoplay: options.autoplay || false,
      speed: options.speed || 300,
      navigation: options.navigation || {},
      pagination: options.pagination || {},
      breakpoints: options.breakpoints || {},
      freeMode: options.freeMode || false,
      ...options
    };

    this.currentIndex = 0;
    this.isAnimating = false;
    this.autoplayTimer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isDragging = false;

    this.init();
  }

  init() {
    this.setupNavigation();
    this.setupPagination();
    this.setupTouch();
    this.handleResize();
    this.updateView();

    if (this.options.autoplay && this.options.autoplay.delay !== undefined) {
      this.startAutoplay();
    }

    window.addEventListener('resize', () => this.handleResize());
  }

  getCurrentSlidesPerView() {
    const width = window.innerWidth;
    let slidesPerView = this.options.slidesPerView;

    // Check breakpoints
    const breakpointKeys = Object.keys(this.options.breakpoints).sort((a, b) => parseInt(a) - parseInt(b));
    for (const breakpoint of breakpointKeys) {
      if (width >= parseInt(breakpoint)) {
        slidesPerView = this.options.breakpoints[breakpoint].slidesPerView || slidesPerView;
      }
    }

    if (slidesPerView === 'auto') {
      return Math.floor(this.container.offsetWidth / this.slides[0]?.offsetWidth) || 1;
    }

    return typeof slidesPerView === 'string' ? parseFloat(slidesPerView) : slidesPerView;
  }

  handleResize() {
    this.updateView();
  }

  updateView() {
    const slidesPerView = this.getCurrentSlidesPerView();
    const spaceBetween = this.options.spaceBetween;
    const containerWidth = this.wrapper.parentElement.offsetWidth;

    if (this.options.freeMode) {
      // Free scroll mode (like logos slider)
      this.wrapper.style.display = 'flex';
      this.wrapper.style.transition = 'none';

      this.slides.forEach(slide => {
        slide.style.minWidth = 'auto';
        slide.style.marginRight = `${spaceBetween}px`;
      });
    } else {
      // Normal slide mode
      const slideWidth = (containerWidth - (spaceBetween * (slidesPerView - 1))) / slidesPerView;

      this.slides.forEach((slide, index) => {
        slide.style.minWidth = `${slideWidth}px`;
        slide.style.marginRight = '0'; // Remove margins for proper carousel positioning
      });

      this.updatePosition();
    }

    this.updateNavigation();
    this.updatePagination();
  }

  updatePosition() {
    if (this.options.freeMode) return;

    const slidesPerView = this.getCurrentSlidesPerView();
    const spaceBetween = this.options.spaceBetween;
    const slideWidth = this.slides[0]?.offsetWidth || 0;
    // For normal slide mode, translate by the slide width (including any margin)
    const translateX = -(this.currentIndex * slideWidth);

    this.wrapper.style.transform = `translate3d(${translateX}px, 0, 0)`;
    this.wrapper.style.transition = this.isAnimating ? `transform ${this.options.speed}ms ease-out` : 'none';
  }

  setupNavigation() {
    if (this.options.navigation.nextEl) {
      this.nextBtn = typeof this.options.navigation.nextEl === 'string'
        ? document.querySelector(this.options.navigation.nextEl)
        : this.options.navigation.nextEl;

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }
    }

    if (this.options.navigation.prevEl) {
      this.prevBtn = typeof this.options.navigation.prevEl === 'string'
        ? document.querySelector(this.options.navigation.prevEl)
        : this.options.navigation.prevEl;

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }
    }
  }

  updateNavigation() {
    const slidesPerView = this.getCurrentSlidesPerView();
    const maxIndex = Math.max(0, this.slides.length - Math.ceil(slidesPerView));

    if (this.prevBtn) {
      this.prevBtn.style.opacity = this.currentIndex === 0 ? '0.3' : '1';
      this.prevBtn.style.pointerEvents = this.currentIndex === 0 ? 'none' : 'auto';
    }

    if (this.nextBtn) {
      this.nextBtn.style.opacity = this.currentIndex >= maxIndex ? '0.3' : '1';
      this.nextBtn.style.pointerEvents = this.currentIndex >= maxIndex ? 'none' : 'auto';
    }
  }

  setupPagination() {
    // Only setup pagination if explicitly enabled with an el selector
    if (this.options.pagination && this.options.pagination.el) {
      this.paginationEl = typeof this.options.pagination.el === 'string'
        ? this.container.querySelector(this.options.pagination.el)
        : this.options.pagination.el;

      if (this.paginationEl) {
        this.updatePagination();
      } else {
        // Try to find pagination as a sibling if not found as child
        this.paginationEl = this.container.parentElement.querySelector(this.options.pagination.el);
        if (this.paginationEl) {
          this.updatePagination();
        }
      }
    }
  }

  updatePagination() {
    if (!this.paginationEl) return;

    const slidesPerView = this.getCurrentSlidesPerView();
    const totalPages = Math.ceil(this.slides.length / Math.ceil(slidesPerView));

    this.paginationEl.innerHTML = '';

    for (let i = 0; i < totalPages; i++) {
      const bullet = document.createElement('span');
      bullet.className = 'swiper-pagination-bullet';
      if (i === Math.floor(this.currentIndex / Math.ceil(slidesPerView))) {
        bullet.classList.add('swiper-pagination-bullet-active');
      }

      if (this.options.pagination.clickable) {
        bullet.style.cursor = 'pointer';
        bullet.addEventListener('click', () => this.slideTo(i * Math.ceil(slidesPerView)));
      }

      this.paginationEl.appendChild(bullet);
    }
  }

  setupTouch() {
    this.wrapper.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
    this.wrapper.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
    this.wrapper.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });

    // Mouse drag support
    this.wrapper.addEventListener('mousedown', (e) => this.handleTouchStart(e));
    this.wrapper.addEventListener('mousemove', (e) => this.handleTouchMove(e));
    this.wrapper.addEventListener('mouseup', (e) => this.handleTouchEnd(e));
    this.wrapper.addEventListener('mouseleave', (e) => this.handleTouchEnd(e));
  }

  handleTouchStart(e) {
    this.isDragging = true;
    this.touchStartX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
    this.stopAutoplay();
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;

    if (e.type === 'touchmove' && !this.options.freeMode) {
      e.preventDefault();
    }
  }

  handleTouchEnd(e) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.touchEndX = e.type === 'mouseup' || e.type === 'mouseleave' ? e.clientX : e.changedTouches[0].clientX;

    const diff = this.touchStartX - this.touchEndX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }

    if (this.options.autoplay && this.options.autoplay.delay !== undefined) {
      this.startAutoplay();
    }
  }

  next() {
    if (this.isAnimating) return;

    const slidesPerView = this.getCurrentSlidesPerView();
    const maxIndex = Math.max(0, this.slides.length - Math.ceil(slidesPerView));

    if (this.currentIndex < maxIndex) {
      this.slideTo(this.currentIndex + 1);
    } else if (this.options.loop) {
      this.slideTo(0);
    }
  }

  prev() {
    if (this.isAnimating) return;

    if (this.currentIndex > 0) {
      this.slideTo(this.currentIndex - 1);
    } else if (this.options.loop) {
      const slidesPerView = this.getCurrentSlidesPerView();
      const maxIndex = Math.max(0, this.slides.length - Math.ceil(slidesPerView));
      this.slideTo(maxIndex);
    }
  }

  slideTo(index) {
    const slidesPerView = this.getCurrentSlidesPerView();
    const maxIndex = Math.max(0, this.slides.length - Math.ceil(slidesPerView));

    this.currentIndex = Math.max(0, Math.min(index, maxIndex));
    this.isAnimating = true;

    this.updatePosition();
    this.updateNavigation();
    this.updatePagination();

    setTimeout(() => {
      this.isAnimating = false;
    }, this.options.speed);
  }

  startAutoplay() {
    if (!this.options.autoplay || this.options.autoplay.delay === undefined) return;

    this.stopAutoplay();

    const animate = () => {
      this.wrapper.style.transition = 'none';
      const currentTransform = this.wrapper.style.transform;
      const currentX = currentTransform ? parseFloat(currentTransform.match(/translate3d\(([^,]+)/)?.[1] || 0) : 0;

      const spaceBetween = this.options.spaceBetween;
      const slideWidth = this.slides[0]?.offsetWidth || 0;
      const moveDistance = slideWidth + spaceBetween;

      this.wrapper.style.transition = `transform ${this.options.speed}ms linear`;
      this.wrapper.style.transform = `translate3d(${currentX - moveDistance}px, 0, 0)`;

      // Check if we need to loop
      setTimeout(() => {
        const totalWidth = this.slides.length * (slideWidth + spaceBetween);
        const halfWidth = totalWidth / 2;

        if (Math.abs(currentX - moveDistance) >= halfWidth) {
          this.wrapper.style.transition = 'none';
          this.wrapper.style.transform = `translate3d(0px, 0, 0)`;
        }

        if (this.options.autoplay && !document.hidden) {
          this.autoplayTimer = setTimeout(animate, this.options.autoplay.delay);
        }
      }, this.options.speed);
    };

    this.autoplayTimer = setTimeout(animate, this.options.autoplay.delay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  destroy() {
    this.stopAutoplay();
    this.slides.forEach(slide => {
      slide.style.minWidth = '';
      slide.style.marginRight = '';
    });
    this.wrapper.style.transform = '';
    this.wrapper.style.transition = '';
  }

  // Public method to disable/enable
  disable() {
    this.wrapper.style.transform = '';
  }

  enable() {
    this.updateView();
  }
}

// Make it globally available
window.LightweightCarousel = LightweightCarousel;
